import { CSSProperties, ForwardedRef, MouseEventHandler, ReactNode } from 'react';

import BasicButton, { BasicButtonElement } from '../buttons/BasicButton';

export type CardContainerElement = HTMLDivElement | HTMLLabelElement | BasicButtonElement;

export interface CardContainerProps {
    href?: string | null;
    external?: boolean;
    target?: string | null;
    isLabel?: boolean;
    labelFor?: string | null;
    brand?: string | null;
    presentation: string;
    theme?: string | null;
    size?: string | null;
    responsive?: boolean;
    style?: CSSProperties | null;
    className?: string | null;
    children?: ReactNode;
    ref?: ForwardedRef<CardContainerElement> | null;
    onClick?: MouseEventHandler<BasicButtonElement> | null;
}

/**
 * Container of the card skeletons: a link or a button (`BasicButton`) when the card is clickable,
 * a `label` for a form card, a `div` otherwise. It sets the data attributes that select the
 * variables of the presentation, the theme and the size (styles/theme/cards.css).
 */
function CardContainer({
    href = null,
    external = false,
    target = null,
    isLabel = false,
    labelFor = null,
    brand = null,
    presentation,
    theme = null,
    size = null,
    responsive = false,
    style = null,
    className = null,
    children = null,
    ref = null,
    onClick = null,
}: CardContainerProps) {
    const dataAttributes = {
        'data-brand': brand !== null ? brand : undefined,
        'data-card-presentation': presentation,
        'data-card-theme': theme !== null ? theme : undefined,
        'data-card-size': size !== null ? size : undefined,
        'data-card-responsive': responsive ? 'true' : undefined,
    };

    if (isLabel) {
        return (
            <label
                className={className}
                htmlFor={labelFor}
                style={style}
                ref={ref as ForwardedRef<HTMLLabelElement>}
                {...dataAttributes}
            >
                {children}
            </label>
        );
    }

    if (href !== null || onClick !== null) {
        return (
            <BasicButton
                href={href}
                external={external}
                target={target}
                onClick={onClick}
                style={style}
                className={className}
                ref={ref as ForwardedRef<BasicButtonElement>}
                {...dataAttributes}
            >
                {children}
            </BasicButton>
        );
    }

    return (
        <div
            className={className}
            style={style}
            ref={ref as ForwardedRef<HTMLDivElement>}
            {...dataAttributes}
        >
            {children}
        </div>
    );
}

export default CardContainer;
